// CommissionerRoute.js
// Route guard component that restricts access to commissioner-level users only.
// Redirects members and unauthorized users to the league dashboard.

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';

/**
 * Wraps a route to ensure only commissioners (or league creators) can access it.
 * If the user is not a commissioner, they are redirected to the league dashboard.
 * 
 * @example
 * // In App.js routes:
 * <Route
 *   path="/league/:leagueId/settings"
 *   element={
 *     <ProtectedRoute>
 *       <CommissionerRoute>
 *         <LeagueSettingsPage />
 *       </CommissionerRoute>
 *     </ProtectedRoute>
 *   }
 * />
 */
function CommissionerRoute({ children, redirectTo }) {
  const { leagueId } = useParams();
  const { isCommissioner, loading, hasAccess, error } = useUserRole(leagueId);
  const navigate = useNavigate();
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    // Wait for role to be determined
    if (loading) return;

    if (error) {
      console.error('CommissionerRoute: Error checking role:', error);
      // Redirect to dashboard on error
      navigate(redirectTo || `/league/${leagueId}/dashboard`, { replace: true });
      return;
    }

    if (!hasAccess) {
      // User has no access to this league at all
      console.warn('CommissionerRoute: User has no access to this league');
      navigate('/', { replace: true });
      return;
    }

    if (!isCommissioner) {
      // User is a member but not a commissioner
      console.info('CommissionerRoute: Member access denied, redirecting to dashboard');
      navigate(redirectTo || `/league/${leagueId}/dashboard`, { replace: true });
      return;
    }

    // User is a commissioner - allow access
    setShouldRender(true);
  }, [loading, isCommissioner, hasAccess, error, leagueId, navigate, redirectTo]);

  // Show loading state while checking permissions
  if (loading) {
    return (
      <div className="loading-container" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
        fontSize: '1rem',
        color: '#6c757d'
      }}>
        <span>Checking permissions...</span>
      </div>
    );
  }

  // Don't render anything if not authorized (redirect will happen)
  if (!shouldRender) {
    return null;
  }

  // Render the protected content
  return <>{children}</>;
}

export default CommissionerRoute;
